import n, { N } from "./n";
import type NItem from "./NItem";
import type { NCssObject } from "./NItem";

export interface PopupConfig {
    content: string,
    updateOnly?: boolean,
};

export interface PopupSetup {
    markup: {
        background: string,
        content: string,
        close: string,
    },
    effects: {
        show: {
            duration: number,
            effect: string,
        },
        hide: {
            duration: number,
            effect: string,
        },
    },
    background: boolean,
};

interface WidthAndHeight {
    width: number,
    height: number,
}

interface PopupDimensionDefinitions {
    document: WidthAndHeight,
    window: WidthAndHeight,
    content: WidthAndHeight,
    background: WidthAndHeight,
}

export default class Popup
{
    #config: PopupConfig = {
        content: '',
        updateOnly: false,
    };
    #style: NCssObject = {
        'box-shadow': '0px 0px 20px 20px #555',
        'width': '1000px',
    };
    #selectors: { [key: string]: string } = {
        content: '.sitesmart-popup-component.sitesmart-site-content',
    };
    #setup: PopupSetup = {
        markup: {
            background: '<div class="sitesmart-popup-wrapper sitesmart-popup-component"></div>',
            content: '<div class="sitesmart-site-content sitesmart-popup-content sitesmart-popup-component"></div>',
            close: '<div class="sitesmart-popup-close"><i class="fi fi-x"></i></div>'
        },
        effects: {
            show: {
                duration: 200,
                effect: 'fade'
            },
            hide: {
                duration: 100,
                effect: 'fade'
            }
        },
        background: true,
    };
    #defaultShowDuration: number = 200;
    #defaultHideDuration: number = 100;
    #background: N;
    #content: N;
    #dimensions: PopupDimensionDefinitions = {
        document: { width: 0, height: 0 },
        window: { width: 0, height: 0 },
        content: { width: 0, height: 0 },
        background: { width: 0, height: 0 },
    };

    constructor(config: PopupConfig, style: NCssObject = {})
    {
        this.#config = { ...this.#config, ...config };
        this.#style = { ...this.#style, ...style };

        let globals: null|PopupSetup = null;

        if (typeof window.SiteSmart_PopupGlobals !== 'undefined') {
            globals = window.SiteSmart_PopupGlobals;
        }

        this.#setup = {
            markup: { ...this.#setup.markup, ...globals?.markup },
            effects: {
                show: { ...this.#setup.effects.show, ...globals?.effects?.show },
                hide: { ...this.#setup.effects.hide, ...globals?.effects?.hide },
            },
            background: globals?.background ?? this.#setup.background,
        };

        this.#background = n(this.#setup.markup.background);
        this.#content = n(this.#setup.markup.content);

        this.#init();
    }

    async #init()
    {
        if (this.#config.updateOnly === false) {
            await this.#addToBody();
            await this.#gatherData();
            await this.#setContent();
            const styles = await this.#setStyle();
            await this.#show();
            await this.#setPosition(styles);
            await this.#addCloseButton();
            this.initEvents();
        } else {
            n(this.#selectors.content).html(this.#config.content);
        }
    }

    async #addToBody(): Promise<void>
    {
        if (n('.sitesmart-popup-wrapper').length > 0) {
            this.#background = n('.sitesmart-popup-wrapper');
        } else {
            n('body').append(this.#background);
        }

        if (n('.sitesmart-popup-content').length > 0) {
            this.#content = n('.sitesmart-popup-content');
        } else {
            n('body').append(this.#content);
        }
    }

    async #gatherData(): Promise<void>
    {
        this.#dimensions = {
            document: {
                width: document.documentElement.clientWidth,
                height: document.documentElement.clientHeight,
            },
            window: {
                width: window.innerWidth,
                height: window.innerHeight,
            },
            content: {
                width: this.#content.width as number,
                height: this.#content.height as number,
            },
            background: {
                width: this.#content.width as number,
                height: this.#content.height as number,
            },
        };
    }

    async #setContent(): Promise<void>
    {
        this.#content?.html?.(this.#config.content);
    }

    async #setStyle(): Promise<NCssObject>
    {
        const css: NCssObject = {
            ...this.#style,
            'background-image': 'none',
        };

        this.#content.css(css);
        this.#background.css('height', `${this.#dimensions.document.height}px`);

        if (this.#setup.background === false) {
            this.#background.css('opacity', '0');
        }

        return css;
    }

    async #show(): Promise<void>
    {
        this.#content.show({
            duration: this.#setup?.effects?.show?.duration ?? this.#defaultShowDuration,
        });
        this.#background.show({
            duration: this.#setup?.effects?.show?.duration ?? this.#defaultHideDuration,
        });

        if (this.#content.height > this.#dimensions.window.height) {
            const newHeight = this.#dimensions.window.height - 50;
            this.#content.css({
                height: `${newHeight}px`,
            });
        }
    }

    async #setPosition(styles: NCssObject)
    {
        const contentWidth = this.#content.width;
        const contentHeight = this.#content.height;
        const setTop = (styles?.top && styles?.top !== '')
            ? styles.top
            : (this.#dimensions.window.height / 2) - (contentHeight / 2);

        this.#content.css({
            left: `${(this.#dimensions.document.width / 2) - (contentWidth / 2)}px`,
            top: `${setTop}px`,
        });
    }

    async #addCloseButton(): Promise<void>
    {
        if (n('body').find('.sitesmart-popup-close').length === 0) {
            n('body').append(this.#setup.markup.close);
        } else {
            n('body').find('.sitesmart-popup-close').show();
        }

        n('body').find('.sitesmart-popup-close').css({
            top: `${this.#content.top}px`,
            left: `${this.#content.left + this.#content.width}px`,
        });
    }

    initEvents(): void
    {
        n('.sitesmart-popup-wrapper')
            .off('click.sitesmartPopupWrapper')
            .on('click.sitesmartPopupWrapper', () => {
                this.close();
            });

        n('body')
            .off('keydown.sitesmartPopupKeydown')
            .on('keydown.sitesmartPopupKeydown', (e) => {
                if ((e as KeyboardEvent).key == 'Escape') {
                    this.close();
                }
            });

        n('body')
            .find('.sitesmart-popup-close')
            .off('click.sitesmartPopupClose')
            .on('click.sitesmartPopupClose', (e) => {
                e.preventDefault();
                this.close();
            });
    }

    close()
    {
        this.#content.hide({
            duration: this.#setup?.effects?.hide?.duration ?? this.#defaultHideDuration,
        }, (item: NItem) => {
            item.remove();
        });
        this.#background.hide({
            duration: this.#setup?.effects?.hide?.duration ?? this.#defaultHideDuration,
        }, (item: NItem) => {
            item.remove();
        });

        n('body').find('.sitesmart-popup-close').hide();

        this.removeEvents();
    }

    removeEvents(): void
    {
        n('.sitesmart-popup-wrapper').off('click.sitesmartPopupWrapper');
        n('body').off('keydown.sitesmartPopupKeydown');
    }
}
